// HaushaltUmzugDialog.tsx — A4 (Plan 2026-09-30): Die Standardanschrift des
// Kontakts hat sich geändert. „Auch bei den Haushaltsmitgliedern ändern?" —
// alle vorausgewählt, jedes einzeln abwählbar (Entscheidung 30.09.2026).
//
// Als Hook wie `useKontaktKonflikt`: `fragen(mitglieder)` liefert ein Promise
// mit den gewählten Mitgliedern (leer = keins), `dialog` wird gerendert.

import * as React from "react";
import { createPortal } from "../windowManager/browserFenster/fensterPortal";
import { Z_LAYERS } from "../windowManager/zLayers";
import type { HaushaltsMitglied } from "../../utils/haushaltUmzug";
import "./contactRedesign.css";

const knopf: React.CSSProperties = {
  appearance: "none",
  border: "1.5px solid #ddd3dc",
  background: "#fff",
  color: "#591149",
  cursor: "pointer",
  font: '700 13.5px/1.2 "Hanken Grotesk", sans-serif',
  padding: "10px 16px",
  borderRadius: 10,
};
const primaer: React.CSSProperties = { ...knopf, background: "#7d2169", borderColor: "#7d2169", color: "#fff" };
const text: React.CSSProperties = { margin: 0, font: '500 14.5px/1.5 "Hanken Grotesk", sans-serif', color: "#241522" };

export function useHaushaltUmzug(): {
  fragen: (mitglieder: HaushaltsMitglied[]) => Promise<HaushaltsMitglied[]>;
  dialog: React.ReactNode;
} {
  const [offen, setOffen] = React.useState<HaushaltsMitglied[] | null>(null);
  const [gewaehlt, setGewaehlt] = React.useState<Set<number>>(new Set());
  const resolveRef = React.useRef<((m: HaushaltsMitglied[]) => void) | null>(null);

  const fragen = React.useCallback((mitglieder: HaushaltsMitglied[]) => {
    resolveRef.current?.([]);
    setOffen(mitglieder);
    setGewaehlt(new Set(mitglieder.map((m) => m.id)));
    return new Promise<HaushaltsMitglied[]>((resolve) => {
      resolveRef.current = resolve;
    });
  }, []);

  const beenden = (uebernehmen: boolean) => {
    const r = resolveRef.current;
    resolveRef.current = null;
    const auswahl = uebernehmen ? (offen ?? []).filter((m) => gewaehlt.has(m.id)) : [];
    setOffen(null);
    r?.(auswahl);
  };

  const dialog = offen
    ? createPortal(
        <div className="kvp-rd kvp-rd-scrim" style={{ zIndex: Z_LAYERS.windowConfirm }} data-testid="umzug-scrim">
          <div
            className="dm"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="umzug-titel"
            style={{ width: "min(520px, 100%)" }}
          >
            <div className="dm-head">
              <div className="dm-head-top">
                <div className="dm-titles">
                  <div className="dm-kicker">Kontaktakte</div>
                  <h2 id="umzug-titel">Anschrift auch im Haushalt ändern?</h2>
                </div>
              </div>
            </div>
            <div className="dm-body" style={{ paddingTop: 4, display: "grid", gap: 12 }}>
              <p style={text}>
                Die Anschrift wurde geändert. Soll sie auch bei diesen Haushaltsmitgliedern gelten? Die bisherige
                Anschrift bleibt dort als weitere Anschrift erhalten.
              </p>
              <div style={{ display: "grid", gap: 8 }}>
                {offen.map((m) => (
                  <label key={m.id} style={{ ...text, display: "flex", gap: 10, alignItems: "center", cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={gewaehlt.has(m.id)}
                      onChange={(e) =>
                        setGewaehlt((alt) => {
                          const neu = new Set(alt);
                          if (e.target.checked) neu.add(m.id);
                          else neu.delete(m.id);
                          return neu;
                        })
                      }
                    />
                    {m.name ?? `Kontakt ${m.id}`}
                  </label>
                ))}
              </div>
            </div>
            <div className="dm-foot" style={{ flexWrap: "wrap" }}>
              <button type="button" style={knopf} onClick={() => beenden(false)}>
                Nicht ändern
              </button>
              <button type="button" style={primaer} disabled={gewaehlt.size === 0} onClick={() => beenden(true)}>
                Bei den gewählten ändern
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )
    : null;

  return { fragen, dialog };
}
